import { FilterGroupType } from 'module/types/filter-field/grouped-filter/group';
import { getApiGroupedFilterTransformer } from 'module/ui/components/molecules/field-filter/filter-transformation/api-grouped-filter-transformer';
import { GroupFieldsMeta } from 'module/ui/components/molecules/field-filter/filter-transformation/filter-transformer';
import { ViewType } from 'api/views/types';
import { useCompanyFieldsAsFilterableField } from 'helpers/fields/company-fields/fields';
import { useSignalFieldsAsFilterableFieldWithReason } from 'helpers/fields/signal-fields/fields';
import { keyBy } from 'lodash';
import {
  useGetViewFiltersQuery,
  useSaveViewFilterMutation,
  useUpdateViewFilterMutation,
} from 'queries/filters';
import { useMemo } from 'react';
import { useAllSignalsContext } from './all-signals-context';

interface UseSavedViewsProps {
  setSelectedSignalUnderMyAccounts: (type: string) => void;
  setSavedViewsSelected: (view: string) => void;
  setSharedViewsSelected: (view: string) => void;
  replaceFilter: (filter: any) => void;
  savedViewsSelected: string;
  sharedViewsSelected: string;
}

export const useSavedViews = ({
  setSelectedSignalUnderMyAccounts,
  setSavedViewsSelected,
  setSharedViewsSelected,
  replaceFilter,
  savedViewsSelected,
  sharedViewsSelected,
}: UseSavedViewsProps) => {
  const { mutate: saveViewFilter } = useSaveViewFilterMutation();
  const { mutate: updateViewFilter } = useUpdateViewFilterMutation();

  const { setCurrent } = useAllSignalsContext();
  const { fields: companyFields } = useCompanyFieldsAsFilterableField();
  const { fields: signalFields } = useSignalFieldsAsFilterableFieldWithReason();

  const companyFieldLookup = useMemo(
    () => keyBy(companyFields, 'fieldName'),
    [companyFields],
  );
  const signalFieldLookup = useMemo(
    () => keyBy(signalFields, 'fieldName'),
    [signalFields],
  );

  const groupedLookupsByName: GroupFieldsMeta = useMemo(() => {
    let fieldsMeta = [
      {
        id: FilterGroupType.Company,
        fields: companyFieldLookup,
      },
      {
        id: FilterGroupType.Signal,
        fields: signalFieldLookup,
      },
    ];

    return fieldsMeta;
  }, [companyFieldLookup, signalFieldLookup]);

  const { data: viewsData } = useGetViewFiltersQuery(
    getApiGroupedFilterTransformer(
      groupedLookupsByName,
      FilterGroupType.Company,
    ) as any,
    ViewType.AllSignal,
    ['all-signals', 'views'],
    {
      enabled: true,
      queryKey: ['all-signals', 'views'],
    },
  );

  const groupedTransformer = useMemo(
    () =>
      getApiGroupedFilterTransformer(
        groupedLookupsByName,
        FilterGroupType.Company,
      ),
    [groupedLookupsByName],
  );

  const transformedViews = useMemo(() => {
    if (!viewsData) {
      return [];
    }

    return viewsData.map((view: any) => ({
      ...view,
      filter: groupedTransformer.from(view.viewDefinition?.filter),
    }));
  }, [viewsData, groupedTransformer]);

  const selectedSavedView = useMemo(() => {
    const selectedViewName = savedViewsSelected || sharedViewsSelected;

    return (transformedViews || ([] as any))?.find(
      (v) => v.viewName === selectedViewName,
    );
  }, [transformedViews, savedViewsSelected, sharedViewsSelected]);

  const handleSaveView = async (view: {
    viewName: string;
    filter: any;
    self?: boolean;
  }) => {
    await saveViewFilter({
      createdBy: -1,
      viewId: -1,
      viewName: view.viewName,
      viewDefinition: {
        filter: groupedTransformer.to(view.filter) as any,
      },
      self: view.self ?? false,
      viewType: ViewType.AllSignal,
    });
  };

  const handleUpdateView = async (view: {
    viewName: string;
    filter: any;
    self?: boolean;
  }) => {
    const selectedViewName = savedViewsSelected || sharedViewsSelected;

    if (!selectedViewName || !selectedSavedView) {
      return;
    }

    await updateViewFilter({
      viewId: selectedSavedView.viewId,
      createdBy: selectedSavedView.createdBy,
      viewName: view.viewName,
      viewDefinition: {
        filter: groupedTransformer.to(view.filter) as any,
      },
      self: view.self ?? false,
      viewType: ViewType.AllSignal,
    });
  };

  const handleSavedViewSelect = (view: any) => {
    setCurrent('');
    const transformedFilter = groupedTransformer.from(
      view?.viewDefinition?.filter,
    );

    const updatedGroupCriteria = transformedFilter.groupCriteria.map(
      (criteria: any) => {
        if (criteria.criteria?.field?.fieldId === 1660316) {
          return {
            ...criteria,
            hidden: true,
          };
        }

        return criteria;
      },
    );

    const finalTransformedFilter = {
      ...transformedFilter,
      groupCriteria: updatedGroupCriteria,
    };

    setSelectedSignalUnderMyAccounts('');

    if (view?.self) {
      setSavedViewsSelected(view?.viewName);
      setSharedViewsSelected('');
    } else {
      setSharedViewsSelected(view?.viewName);
      setSavedViewsSelected('');
    }
    // console.log('transformedFilter', finalTransformedFilter);
    replaceFilter(finalTransformedFilter);
  };

  return {
    views: transformedViews || [],
    groupedTransformer,
    handleSaveView,
    handleUpdateView,
    handleSavedViewSelect,
    selectedSavedView,
  };
};
